import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import elumeLogo from "./assets/ELogo2.png";
import CollabBoard from "./CollabBoard";
import type { BoardSnapshot } from "./CollabBoard";
import {
    COLLABORATION_TEMPLATE_SUBJECTS,
    templatesForSubject,
    type CollaborationTemplate,
    type CollaborationTemplateSubject,
} from "./collaborationTemplates";
import { apiFetch, isDemoSession } from "./api";
import DepartmentShareModal from "./Components/DepartmentShareModal";
import InlineNotice from "./Components/InlineNotice";
import { userFacingError } from "./userFacingError";
import { TeacherToolPalette } from "./TeacherToolPalette";
import { TeacherHighlighterSettings } from "./TeacherHighlighterSettings";
import { ColourSwatch, StrokeSizeButton } from "./BoardControlOptions";
import { Maximize2, Minimize2, UsersRound, X } from "lucide-react";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import collabBoardStartArt from "./assets/teacher-activities/collab-board-start.png";
import { CollabGroupArt } from "./Components/StudentHubActivityArt";
import { committedCollabRoomCount, MAX_COLLAB_ROOM_COUNT, MIN_COLLAB_ROOM_COUNT } from "./collabRoomCount";

const API_BASE = "/api";
const MIN_ROOM_COUNT = MIN_COLLAB_ROOM_COUNT;
const MAX_ROOM_COUNT = MAX_COLLAB_ROOM_COUNT;

type CollabCreateResponse = {
    session_code: string;
    join_url?: string | null;
};

type CollabStatus = {
    session_code: string;
    title: string;
    state: "lobby" | "assigning" | "live" | "review" | "ended";
    room_count: number;
    timer_minutes?: number | null;
    time_left_seconds?: number | null;
    board_round?: number;
    joined_count: number;
    assigned_count: number;
};

type CollabParticipantApi = {
    id: number;
    anon_id: string;
    name: string;
    room_number: number | null;
    is_online: boolean;
};

type ToolKey =
    | "select"
    | "pen"
    | "eraser"
    | "highlighter"
    | "rectangle"
    | "circle"
    | "triangle"
    | "speech"
    | "sticky"
    | "arrow"
    | "curved-arrow"
    | "pdf";

type PenColor = "black" | "red" | "blue" | "yellow" | "green" | "purple";
type PenSize = 1 | 2 | 3;
type EraserSize = 1 | 2 | 3;
type HighlightColor = "yellow" | "green" | "blue" | "violet";

type CollabParticipant = {
    id: string;
    name: string;
    joinedAt: string;
    roomNumber: number | null;
    isOnline: boolean;
};

type BreakoutRoom = {
    roomNumber: number;
    participantIds: string[];
};

type ReviewPanel = {
    id: string;
    selectedBoard: string;
};

type BoardOption = {
    value: string;
    label: string;
};

type SessionState = "draft" | "lobby" | "assigning" | "live" | "review" | "ended";
type SavedBoard = {
    id: number;
    title: string;
    source_class_id: number | null;
    room_count: number;
    timer_minutes: number | null;
    created_at: string;
    updated_at: string;
};

type CollaborationNotice = {
    variant: "error" | "warning" | "info";
    title?: string;
    message: string;
};

function uid(prefix = "id") {
    return `${prefix}_${Math.random().toString(36).slice(2, 9)}_${Date.now().toString(36)}`;
}

function cls(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(" ");
}

function buildRoomsFromParticipants(participants: CollabParticipant[], roomCount: number): BreakoutRoom[] {
    return Array.from({ length: roomCount }, (_, i) => ({
        roomNumber: i + 1,
        participantIds: participants.filter((p) => p.roomNumber === i + 1).map((p) => p.id),
    }));
}

function autoAssignParticipants(participants: CollabParticipant[], roomCount: number): CollabParticipant[] {
    const next = participants.map((p) => ({ ...p }));
    const unassigned = next.filter((p) => p.isOnline && p.roomNumber == null);

    unassigned.forEach((p, index) => {
        p.roomNumber = (index % roomCount) + 1;
    });

    return next;
}

function JoinChip({
    label,
    value,
    onCopy,
    large = false,
}: {
    label: string;
    value: string;
    onCopy: () => void;
    large?: boolean;
}) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>

            {large ? (
                <div className="mt-3 min-w-0 break-all text-center font-mono text-[clamp(2rem,10vw,3.75rem)] font-black leading-tight tracking-[0.08em] text-slate-900">
                    {value}
                </div>
            ) : (
                <div className="mt-2 break-all text-sm font-semibold text-slate-800 md:text-base">
                    {value}
                </div>
            )}

            <button
                type="button"
                onClick={onCopy}
                className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-100"
            >
                Copy
            </button>
        </div>
    );
}


function SectionCard({
    title,
    hint,
    right,
    children,
    className,
    bodyClassName,
    id,
}: {
    title: string;
    hint?: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    bodyClassName?: string;
    id?: string;
}) {
    return (
        <div id={id} className={cls("rounded-[32px] border border-white/70 bg-white/85 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.07)] backdrop-blur-xl", className)}>
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xl font-black tracking-tight text-slate-900">{title}</div>
                    {hint ? <div className="mt-1 text-sm text-slate-600">{hint}</div> : null}
                </div>
                {right}
            </div>
            <div className={cls("mt-5", bodyClassName)}>{children}</div>
        </div>
    );
}

export default function CollaborationPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useUiLanguage();

    const classId = Number(id || 0);
    // The public-demo token is tab-scoped and is the earliest reliable signal
    // available to this screen. The API repeats the capability check before
    // any durable collaboration write, so this is only the friendly, early UI
    // guard rather than the security boundary.
    const demoReadOnly = isDemoSession();

    const [sessionTitle, setSessionTitle] = useState("Collaboration Whiteboard");
    const [sessionState, setSessionState] = useState<SessionState>("draft");
    const [isPreparingBoard, setIsPreparingBoard] = useState(false);
    const [preparedBoardId, setPreparedBoardId] = useState<number | null>(null);
    const [preparedBoardSnapshot, setPreparedBoardSnapshot] = useState<BoardSnapshot | null>(null);
    const [boardEditorGeneration, setBoardEditorGeneration] = useState(0);

    const [tool, setTool] = useState<ToolKey>("pen");
    const [penColor, setPenColor] = useState<PenColor>("black");
    const [penSize, setPenSize] = useState<PenSize>(1);
    const [eraserSize, setEraserSize] = useState<EraserSize>(1);
    const [highlightColor, setHighlightColor] = useState<HighlightColor>("yellow");
    const [pdfImportRequestNonce, setPdfImportRequestNonce] = useState(0);
    const [teacherBoardClearNonce, setTeacherBoardClearNonce] = useState(0);
    const [isPreparingNextRound, setIsPreparingNextRound] = useState(false);
    const [boardRound, setBoardRound] = useState(1);

    const [showJoinModal, setShowJoinModal] = useState(false);
    const [showBreakoutModal, setShowBreakoutModal] = useState(false);

    const [roomCount, setRoomCount] = useState(4);
    const [roomCountInput, setRoomCountInput] = useState("4");
    const [roomCountError, setRoomCountError] = useState<string | null>(null);
    const [timerMinutes, setTimerMinutes] = useState(10);
    const [timeLeftSeconds, setTimeLeftSeconds] = useState<number | null>(null);

    const [participants, setParticipants] = useState<CollabParticipant[]>([]);
    const [sessionCode, setSessionCode] = useState<string>("");
    const [status, setStatus] = useState<CollabStatus | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [notice, setNotice] = useState<CollaborationNotice | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isStartingBreakout, setIsStartingBreakout] = useState(false);
    const [savedBoards, setSavedBoards] = useState<SavedBoard[]>([]);
    const [showSavedBoardsModal, setShowSavedBoardsModal] = useState(false);
    const [showSaveBoardModal, setShowSaveBoardModal] = useState(false);
    const [savedBoardTitle, setSavedBoardTitle] = useState("");
    const [isSavingBoard, setIsSavingBoard] = useState(false);
    const [isUsingSavedBoard, setIsUsingSavedBoard] = useState(false);
    const [sharingTemplateId, setSharingTemplateId] = useState<number | null>(null);
    const [showElumeTemplatesModal, setShowElumeTemplatesModal] = useState(false);
    const [templateSubject, setTemplateSubject] = useState<CollaborationTemplateSubject>("Teaching Templates");
    const [templatePreview, setTemplatePreview] = useState<CollaborationTemplate | null>(null);
    const [selectedElumeTemplate, setSelectedElumeTemplate] = useState<CollaborationTemplate | null>(null);
    const [templateRequestId, setTemplateRequestId] = useState<number | undefined>(undefined);
    const [isTeacherFullscreen, setIsTeacherFullscreen] = useState(false);
    const [teacherFullscreenHeight, setTeacherFullscreenHeight] = useState(760);
    const [studentsDrawerOpen, setStudentsDrawerOpen] = useState(false);


    const pollRef = useRef<number | null>(null);
    const joinCodeRef = useRef("");
    const preparedBoardBaselineRef = useRef<BoardSnapshot>({ strokes: [], objects: [] });
    const teacherWorkspaceRef = useRef<HTMLDivElement | null>(null);
    const fullscreenButtonRef = useRef<HTMLButtonElement | null>(null);
    const wasTeacherFullscreenRef = useRef(false);

    const btnGlow =
        "relative inline-flex items-center gap-3 rounded-2xl border-2 border-violet-600 " +
        "bg-gradient-to-r from-violet-500 via-purple-500 to-emerald-500 " +
        "px-7 py-3 text-base font-extrabold text-white " +
        "shadow-[0_0_18px_rgba(120,120,120,0.35)] " +
        "ring-4 ring-slate-300/60 " +
        "hover:shadow-[0_0_40px_rgba(120,120,120,0.6)] hover:ring-slate-400 " +
        "hover:-translate-y-[2px] hover:scale-[1.03] active:scale-[0.98] " +
        "transition-all duration-200 overflow-hidden " +
        "after:absolute after:top-0 after:left-[-60%] after:h-full after:w-[60%] " +
        "after:bg-gradient-to-r after:from-transparent after:via-white/40 after:to-transparent " +
        "after:rotate-12 hover:after:left-[120%] after:transition-all after:duration-700";


    const [rooms, setRooms] = useState<BreakoutRoom[]>(
        Array.from({ length: 4 }, (_, i) => ({ roomNumber: i + 1, participantIds: [] }))
    );

    const [reviewPanels, setReviewPanels] = useState<ReviewPanel[]>([
        { id: uid("panel"), selectedBoard: "room-1" },
        { id: uid("panel"), selectedBoard: "room-2" },
        { id: uid("panel"), selectedBoard: "room-3" },
        { id: uid("panel"), selectedBoard: "room-4" },
    ]);

    const teacherBoardExportRef = useRef<null | (() => Promise<void>)>(null);
    const teacherBoardSnapshotRef = useRef<null | (() => BoardSnapshot)>(null);
    const reviewBoardExportRefs = useRef<Record<string, () => Promise<void>>>({});
    const reviewBoardSnapshotImageRefs = useRef<Record<string, () => Promise<Blob>>>({});
    const archiveOperationIdsRef = useRef<Record<string, string>>({});
    const focusedReviewBoardExportRef = useRef<null | (() => Promise<void>)>(null);

    const [focusedReviewBoard, setFocusedReviewBoard] = useState<string | null>(null);
    const [selectedReviewBoards, setSelectedReviewBoards] = useState<string[]>([]);
    const [archiveFailures, setArchiveFailures] = useState<Record<string, string>>({});
    const [isArchivingBoards, setIsArchivingBoards] = useState(false);
    const joinCode = sessionCode;
    const hasSession = Boolean(joinCode);

    function showError(error: unknown, fallback: string) {
        setNotice({
            variant: "error",
            title: "That didn’t quite work",
            message: userFacingError(error, fallback),
        });
    }

    function showWarning(message: string) {
        setNotice({ variant: "warning", message });
    }

    function explainDemoSaveRestriction() {
        window.dispatchEvent(new CustomEvent("elume-demo-restricted", {
            detail: {
                message: "This demo lets you try temporary collaboration. In your own Elume account, you can save collaboration boards and share them with your class.",
            },
        }));
    }

    function enterTeacherFullscreen() {
        const workspace = teacherWorkspaceRef.current;
        if (!workspace) return;

        // Set the application fallback immediately. A rejected native request still leaves
        // the teacher with the same expanded, tool-complete workspace.
        setIsTeacherFullscreen(true);
        if (typeof workspace.requestFullscreen === "function") {
            workspace.requestFullscreen().catch(() => {
                // The fixed-position focus workspace is intentionally the fallback.
            });
        }
    }

    function exitTeacherFullscreen() {
        if (document.fullscreenElement === teacherWorkspaceRef.current && typeof document.exitFullscreen === "function") {
            document.exitFullscreen().catch(() => setIsTeacherFullscreen(false));
            return;
        }
        setIsTeacherFullscreen(false);
    }

    function showStartBreakoutFailure(error: unknown) {
        if (error instanceof Error && error.message === "No session code available. Create the session first.") {
            showWarning("Create a Collaboration session before starting breakout rooms.");
            return;
        }
        showError(error, "We couldn’t start the breakout rooms just now. Please try again.");
    }

    useEffect(() => {
        joinCodeRef.current = joinCode;
    }, [joinCode]);

    useEffect(() => {
        const syncFullscreenState = () => {
            if (document.fullscreenElement !== teacherWorkspaceRef.current) {
                setIsTeacherFullscreen(false);
            }
        };

        document.addEventListener("fullscreenchange", syncFullscreenState);
        return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
    }, []);

    useEffect(() => {
        if (!isTeacherFullscreen) return;

        const updateHeight = () => {
            // The board's own header and the compact focus header remain above this viewport.
            setTeacherFullscreenHeight(Math.max(420, window.innerHeight - 154));
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [isTeacherFullscreen]);

    useEffect(() => {
        if (!isTeacherFullscreen) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && document.fullscreenElement !== teacherWorkspaceRef.current) {
                setIsTeacherFullscreen(false);
            }
        };

        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [isTeacherFullscreen]);

    useEffect(() => {
        if (!isTeacherFullscreen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isTeacherFullscreen]);

    useEffect(() => {
        if (wasTeacherFullscreenRef.current && !isTeacherFullscreen) {
            fullscreenButtonRef.current?.focus();
        }
        wasTeacherFullscreenRef.current = isTeacherFullscreen;
    }, [isTeacherFullscreen]);

    useEffect(() => {
        if (hasSession) return;
        setIsTeacherFullscreen(false);
    }, [hasSession]);

    const joinUrl = useMemo(
        () => (joinCode ? `${window.location.origin}/#/collab/join/${joinCode}` : ""),
        [joinCode]
    );

    const qrUrl = useMemo(
        () =>
            `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(joinUrl)}`,
        [joinUrl]
    );

    const boardOptions: BoardOption[] = useMemo(() => {
        const teacherBoard = [{ value: "teacher-main", label: "Teacher Main Board" }];
        const roomBoards = Array.from({ length: roomCount }, (_, i) => ({
            value: `room-${i + 1}`,
            label: `Breakout Room ${i + 1}`,
        }));
        return [...teacherBoard, ...roomBoards];
    }, [roomCount]);

    const breakoutBoardOptions = useMemo(
        () => boardOptions.filter((board) => board.value.startsWith("room-")),
        [boardOptions]
    );

    useEffect(() => {
        return () => stopPolling();
    }, []);

    useEffect(() => {
        if (!joinCode) {
            stopPolling();
            return;
        }

        stopPolling();

        void fetchStatus(joinCode);
        if (!showBreakoutModal) {
            void fetchParticipants(joinCode);
        }

        pollRef.current = window.setInterval(() => {
            const currentCode = joinCodeRef.current;
            if (!currentCode) return;
            void fetchStatus(currentCode);
            if (!showBreakoutModal) {
                void fetchParticipants(currentCode);
            }
        }, 1000);

        return () => stopPolling();
    }, [joinCode, showBreakoutModal]);

    useEffect(() => {
        setRooms((prev) => {
            const next = Array.from({ length: roomCount }, (_, i) => {
                const existing = prev.find((r) => r.roomNumber === i + 1);
                return existing || { roomNumber: i + 1, participantIds: [] };
            });
            return next;
        });

        setReviewPanels((prev) =>
            prev.map((p, idx) => ({
                ...p,
                selectedBoard: `room-${Math.min(idx + 1, roomCount)}`,
            }))
        );

        setFocusedReviewBoard(null);
    }, [roomCount]);

    useEffect(() => {
        setRooms(buildRoomsFromParticipants(participants, roomCount));
    }, [participants, roomCount]);

    useEffect(() => {
        if (timeLeftSeconds === null || timeLeftSeconds <= 0) return;

        const timer = window.setInterval(() => {
            setTimeLeftSeconds((curr) => {
                if (curr === null) return null;
                if (curr <= 1) {
                    window.clearInterval(timer);
                    return 0;
                }
                return curr - 1;
            });
        }, 1000);

        return () => window.clearInterval(timer);
    }, [timeLeftSeconds]);

    function stopPolling() {
        if (pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }

    function copyToClipboard(text: string) {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => { });
            return;
        }
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    }

    function formatTime(totalSeconds: number | null) {
        if (totalSeconds === null) return "—";
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${String(secs).padStart(2, "0")}`;
    }

    async function fetchStatus(code: string) {
        try {
            const res = await fetch(`${API_BASE}/collab/${code}/status`);
            if (!res.ok) {
                if (res.status === 404) {
                    throw new Error("No session yet. Create a session first.");
                }
                throw new Error("Status unavailable.");
            }

            const data = (await res.json()) as CollabStatus;
            setStatus(data);
            setSessionState(data.state);
            setBoardRound(Math.max(1, Number(data.board_round || 1)));

            // Only hydrate from backend before a teacher edits locally,
            // or if you want backend to remain the source of truth.
            // For now: do not force roomCount back on every poll.
            setTimeLeftSeconds(data.time_left_seconds ?? null);
            setStatusError(null);
        } catch (error: unknown) {
            setStatusError(userFacingError(error, "Live updates couldn’t refresh just now. Check your connection if this continues."));
        }
    }

    async function fetchParticipants(code: string) {
        try {
            const data = await apiFetch(`${API_BASE}/collab/${code}/participants`);
            const list = (data?.participants || []) as CollabParticipantApi[];
            setParticipants(
                list.map((p) => ({
                    id: String(p.id),
                    name: p.name,
                    joinedAt: "",
                    roomNumber: p.room_number,
                    isOnline: p.is_online,
                }))
            );
        } catch (error: unknown) {
            setStatusError(userFacingError(error, "Live updates couldn’t refresh just now. Check your connection if this continues."));
        }

    }

    async function fetchSavedBoards() {
        setNotice(null);
        try {
            const data = await apiFetch(`${API_BASE}/collab/templates`);
            setSavedBoards(Array.isArray(data?.templates) ? data.templates : []);
        } catch (error: unknown) {
            showError(error, "We couldn’t load your saved boards just yet. Give it another try.");
        }
    }

    function selectElumeTemplate(template: CollaborationTemplate | null) {
        setSelectedElumeTemplate(template);
        setTemplateRequestId((current) => (current ?? 0) + 1);
        setTemplatePreview(null);
        setShowElumeTemplatesModal(false);
    }

    async function launchSavedBoard(template: SavedBoard) {
        if (isUsingSavedBoard) return;
        setNotice(null);
        setIsUsingSavedBoard(true);
        try {
            const data = (await apiFetch(`${API_BASE}/collab/templates/${template.id}/use`, {
                method: "POST",
                body: JSON.stringify({ class_id: classId }),
            })) as CollabCreateResponse;
            if (!data.session_code) throw new Error("Could not start saved board.");
            setSessionTitle(template.title);
            setRoomCount(template.room_count);
            setRoomCountInput(String(template.room_count));
            setTimerMinutes(template.timer_minutes ?? 10);
            setSessionCode(data.session_code);
            setSessionState("lobby");
            setBoardRound(1);
            setShowSavedBoardsModal(false);
            await Promise.all([fetchStatus(data.session_code), fetchParticipants(data.session_code)]);
            setShowJoinModal(true);
        } catch (error: unknown) {
            showError(error, "We couldn’t open that saved board just now. Please try again.");
        } finally {
            setIsUsingSavedBoard(false);
        }
    }

    function beginBoardPreparation() {
        const blank: BoardSnapshot = { strokes: [], objects: [] };
        preparedBoardBaselineRef.current = blank;
        setPreparedBoardId(null);
        setPreparedBoardSnapshot(blank);
        setSessionTitle("Prepared collaboration board");
        setSelectedElumeTemplate(null);
        setTemplateRequestId(undefined);
        setIsPreparingBoard(true);
        setBoardEditorGeneration((generation) => generation + 1);
    }

    async function editSavedBoard(template: SavedBoard) {
        setIsUsingSavedBoard(true);
        setNotice(null);
        try {
            const data = await apiFetch(`${API_BASE}/collab/templates/${template.id}`);
            const snapshot = data?.snapshot as BoardSnapshot | undefined;
            if (!snapshot || !Array.isArray(snapshot.strokes) || !Array.isArray(snapshot.objects)) {
                throw new Error("The saved board could not be read.");
            }
            const safeSnapshot: BoardSnapshot = {
                strokes: snapshot.strokes,
                objects: snapshot.objects,
            };
            preparedBoardBaselineRef.current = JSON.parse(JSON.stringify(safeSnapshot)) as BoardSnapshot;
            setPreparedBoardSnapshot(safeSnapshot);
            setPreparedBoardId(template.id);
            setSessionTitle(template.title);
            setRoomCount(template.room_count);
            setRoomCountInput(String(template.room_count));
            setTimerMinutes(template.timer_minutes ?? 10);
            setSelectedElumeTemplate(null);
            setTemplateRequestId(undefined);
            setIsPreparingBoard(true);
            setBoardEditorGeneration((generation) => generation + 1);
            setShowSavedBoardsModal(false);
        } catch (error: unknown) {
            showError(error, "We couldn’t open that saved board for editing. Please try again.");
        } finally {
            setIsUsingSavedBoard(false);
        }
    }

    function leaveBoardPreparation() {
        if (!demoReadOnly) {
            const currentSnapshot = teacherBoardSnapshotRef.current?.();
            const baseline = preparedBoardBaselineRef.current;
            if (currentSnapshot && JSON.stringify(currentSnapshot) !== JSON.stringify(baseline)) {
                const leave = window.confirm(t("collab.preparationUnsaved"));
                if (!leave) return;
            }
        }
        setIsPreparingBoard(false);
        teacherBoardSnapshotRef.current = null;
    }

    async function renameSavedBoard(template: SavedBoard) {
        if (demoReadOnly) {
            explainDemoSaveRestriction();
            return;
        }
        const title = window.prompt("Rename saved board", template.title)?.trim();
        if (!title || title === template.title) return;
        setNotice(null);
        try {
            await apiFetch(`${API_BASE}/collab/templates/${template.id}`, {
                method: "PATCH",
                body: JSON.stringify({ title }),
            });
            await fetchSavedBoards();
        } catch (error: unknown) {
            showError(error, "We couldn’t rename that saved board just now. Please try again.");
        }
    }

    async function deleteSavedBoard(template: SavedBoard) {
        if (demoReadOnly) {
            explainDemoSaveRestriction();
            return;
        }
        if (!window.confirm(`Delete saved board "${template.title}"? This will not affect any live session.`)) return;
        setNotice(null);
        try {
            await apiFetch(`${API_BASE}/collab/templates/${template.id}`, { method: "DELETE" });
            await fetchSavedBoards();
        } catch (error: unknown) {
            showError(error, "We couldn’t delete that saved board just now. Please try again.");
        }
    }

    async function saveBoardForLater() {
        if (demoReadOnly) {
            explainDemoSaveRestriction();
            return;
        }
        const title = savedBoardTitle.trim();
        if (!title || isSavingBoard) return;
        setNotice(null);
        setIsSavingBoard(true);
        try {
            if (isPreparingBoard) {
                const snapshot = teacherBoardSnapshotRef.current?.();
                if (!snapshot) throw new Error("The board is still loading. Please try again.");
                const body = JSON.stringify({
                    title,
                    class_id: classId,
                    room_count: roomCount,
                    timer_minutes: timerMinutes,
                    snapshot,
                });
                const result = preparedBoardId
                    ? await apiFetch(`${API_BASE}/collab/templates/${preparedBoardId}/board`, { method: "PUT", body })
                    : await apiFetch(`${API_BASE}/collab/templates`, { method: "POST", body });
                if (!result?.template?.id) throw new Error("The saved board was not returned by the server.");
                setPreparedBoardId(Number(result.template.id));
                preparedBoardBaselineRef.current = JSON.parse(JSON.stringify(snapshot)) as BoardSnapshot;
                setPreparedBoardSnapshot(snapshot);
                setSessionTitle(title);
            } else {
                const code = joinCodeRef.current;
                if (!code) return;
                await apiFetch(`${API_BASE}/collab/${code}/save-template`, {
                    method: "POST",
                    body: JSON.stringify({ title }),
                });
            }
            setShowSaveBoardModal(false);
            setSavedBoardTitle("");
            await fetchSavedBoards();
        } catch (error: unknown) {
            showError(error, "We couldn’t save this board just now. Please try again.");
        } finally {
            setIsSavingBoard(false);
        }
    }

    async function persistAssignments(code: string, nextParticipants: CollabParticipant[]) {
        const assignments = nextParticipants.map((p) => ({
            participant_id: Number(p.id),
            room_number: p.roomNumber,
        }));

        await apiFetch(`${API_BASE}/collab/${code}/assignments`, {
            method: "POST",
            body: JSON.stringify({ assignments }),
        });
    }

    function commitRoomCount(): number | null {
        const next = committedCollabRoomCount(roomCountInput);
        if (next === null) {
            setRoomCountError(t("collab.roomCountInvalid"));
            return null;
        }
        setRoomCount(next);
        setRoomCountInput(String(next));
        setRoomCountError(null);
        return next;
    }

    function adjustRoomCount(delta: -1 | 1) {
        const committed = commitRoomCount();
        const base = committed ?? roomCount;
        const next = Math.max(MIN_ROOM_COUNT, Math.min(MAX_ROOM_COUNT, base + delta));
        setRoomCount(next);
        setRoomCountInput(String(next));
        setRoomCountError(null);
    }

    async function syncBreakoutConfig(code: string, nextRoomCount = roomCount) {
        await apiFetch(`${API_BASE}/collab/${code}/config`, {
            method: "POST",
            body: JSON.stringify({
                room_count: nextRoomCount,
                timer_minutes: timerMinutes,
            }),
        });
    }


    async function createSession() {
        if (isCreating) return;
        setNotice(null);
        setIsCreating(true);

        try {
            const data = (await apiFetch(`${API_BASE}/collab/create`, {
                method: "POST",
                body: JSON.stringify({
                    class_id: classId,
                    title: sessionTitle,
                    room_count: roomCount,
                    timer_minutes: timerMinutes,
                }),
            })) as CollabCreateResponse;
            if (!data.session_code) {
                throw new Error("Backend returned no session code.");
            }

            setSessionCode(data.session_code);
            setSessionState("lobby");
            await Promise.all([fetchStatus(data.session_code), fetchParticipants(data.session_code)]);
            setShowJoinModal(true);
        } catch (error: unknown) {
            showError(error, "We couldn’t create this Collaboration session just now. Please try again.");
        } finally {
            setIsCreating(false);
        }
    }

    async function assignAndSave(participantId: string, roomNumber: number | null) {
        const code = joinCodeRef.current;
        if (!code) {
            showWarning("Create a Collaboration session before assigning students to rooms.");
            return;
        }

        setNotice(null);

        const nextParticipants = participants.map((p) =>
            p.id === participantId ? { ...p, roomNumber } : p
        );

        setParticipants(nextParticipants);

        try {
            await syncBreakoutConfig(code);
            await persistAssignments(code, nextParticipants);
            await Promise.all([fetchParticipants(code), fetchStatus(code)]);
        } catch (error: unknown) {
            showError(error, "We couldn’t save the room assignments just now. Please try again.");
        }
    }


    async function saveAssignments() {
        const code = joinCodeRef.current;
        if (!code) {
            showWarning("Create a Collaboration session before saving room assignments.");
            return;
        }

        setNotice(null);

        try {
            await syncBreakoutConfig(code);
            await persistAssignments(code, participants);
            await Promise.all([fetchParticipants(code), fetchStatus(code)]);
        } catch (error: unknown) {
            showError(error, "We couldn’t save the room assignments just now. Please try again.");
        }
    }

    function randomEvenSplit() {
        const shuffledEligible = participants.filter((p) => p.isOnline).sort(() => Math.random() - 0.5);
        const roomById = new Map(shuffledEligible.map((p, idx) => [p.id, (idx % roomCount) + 1]));
        const nextParticipants = participants.map((p) => ({
            ...p,
            roomNumber: p.isOnline ? (roomById.get(p.id) ?? null) : null,
        }));

        const restoredOrder = participants.map((original) => {
            const updated = nextParticipants.find((p) => p.id === original.id);
            return updated || original;
        });

        setParticipants(restoredOrder);
    }

    function clearAssignments() {
        setParticipants((prev) => prev.map((p) => ({ ...p, roomNumber: null })));
    }

    async function startBreakoutAndPersist() {
        const code = joinCodeRef.current;
        if (!code) {
            throw new Error("No session code available. Create the session first.");
        }

        if (isStartingBreakout) return;
        const nextRoomCount = commitRoomCount();
        if (nextRoomCount === null) {
            throw new Error(t("collab.roomCountInvalid"));
        }
        setNotice(null);
        setIsStartingBreakout(true);

        try {
            const nextParticipants = autoAssignParticipants(participants, nextRoomCount);
            const teacherMainSnapshot = teacherBoardSnapshotRef.current?.() ?? null;

            setParticipants(nextParticipants);
            setRooms(buildRoomsFromParticipants(nextParticipants, nextRoomCount));

            await syncBreakoutConfig(code, nextRoomCount);
            await persistAssignments(code, nextParticipants);

            await apiFetch(`${API_BASE}/collab/${code}/start`, {
                method: "POST",
                // The backend captures this once as the immutable teacher-only
                // source before students receive their room copies.
                body: JSON.stringify({ snapshot: teacherMainSnapshot }),
            });

            await Promise.all([fetchStatus(code), fetchParticipants(code)]);
            setIsPreparingNextRound(false);
            setSessionState("live");
            setShowBreakoutModal(false);
            setTimeLeftSeconds(timerMinutes * 60);
        } finally {
            setIsStartingBreakout(false);
        }
    }


    async function endBreakout() {
        const code = joinCodeRef.current;
        if (!code) {
            showWarning("Start a Collaboration session before ending breakout rooms.");
            return;
        }

        setNotice(null);
        try {
            await apiFetch(`${API_BASE}/collab/${code}/end`, {
                method: "POST",
            });

            await Promise.all([fetchStatus(code), fetchParticipants(code)]);
            setIsPreparingNextRound(false);
            setSessionState("review");
            setTimeLeftSeconds(null);
        } catch (error: unknown) {
            showError(error, "We couldn’t end the breakout rooms just now. Please try again.");
        }
    }

    async function downloadTeacherPng() {
        if (!teacherBoardExportRef.current) {
            showWarning("The teacher board is getting ready to export. Please try again in a moment.");
            return;
        }

        setNotice(null);
        try {
            await teacherBoardExportRef.current();
        } catch (error: unknown) {
            showError(error, "We couldn’t export this board just now. Please try again.");
        }
    }

    async function downloadReviewBoardPng(boardKey: string) {
        const fn = reviewBoardExportRefs.current[boardKey];
        if (!fn) {
            showWarning("This board is getting ready to export. Please try again in a moment.");
            return;
        }

        setNotice(null);
        try {
            await fn();
        } catch (error: unknown) {
            showError(error, "We couldn’t export this board just now. Please try again.");
        }
    }

    async function downloadFocusedReviewBoardPng() {
        if (!focusedReviewBoardExportRef.current) {
            showWarning("This board is getting ready to export. Please try again in a moment.");
            return;
        }

        setNotice(null);
        try {
            await focusedReviewBoardExportRef.current();
        } catch (error: unknown) {
            showError(error, "We couldn’t export this board just now. Please try again.");
        }
    }

    function toggleReviewBoardArchive(boardKey: string) {
        setArchiveFailures((previous) => {
            const next = { ...previous };
            delete next[boardKey];
            return next;
        });
        setSelectedReviewBoards((previous) =>
            previous.includes(boardKey)
                ? previous.filter((key) => key !== boardKey)
                : [...previous, boardKey]
        );
    }

    function selectAllReviewBoards() {
        setArchiveFailures({});
        setSelectedReviewBoards(breakoutBoardOptions.map((board) => board.value));
    }

    function clearReviewBoardSelection() {
        setSelectedReviewBoards([]);
        setArchiveFailures({});
    }

    async function saveSelectedReviewBoardsToNotes() {
        // Never render a review board to a PNG, allocate an operation ID, or
        // start an upload in a public demo. The backend independently rejects
        // this path so direct requests cannot bypass the UI.
        if (demoReadOnly) {
            explainDemoSaveRestriction();
            return;
        }
        const code = joinCodeRef.current;
        const selected = [...selectedReviewBoards];
        if (!code || selected.length === 0 || isArchivingBoards) {
            if (selected.length === 0) showWarning("Select at least one breakout board to save to Notes.");
            return;
        }

        setNotice(null);
        setIsArchivingBoards(true);
        const successful: string[] = [];
        const failures: Record<string, string> = {};

        for (const boardKey of selected) {
            const roomNumber = Number(boardKey.replace("room-", ""));
            const renderSnapshot = reviewBoardSnapshotImageRefs.current[boardKey];
            if (!Number.isInteger(roomNumber) || roomNumber < 1 || !renderSnapshot) {
                failures[boardKey] = "This board is still getting ready. Please try again in a moment.";
                continue;
            }

            try {
                const image = await renderSnapshot();
                const operationId = archiveOperationIdsRef.current[boardKey] || uid("archive");
                archiveOperationIdsRef.current[boardKey] = operationId;
                const form = new FormData();
                form.append("room_number", String(roomNumber));
                form.append("operation_id", operationId);
                form.append("snapshot", new File([image], `${boardKey}.png`, { type: "image/png" }));
                await apiFetch(`${API_BASE}/collab/${encodeURIComponent(code)}/archive-board`, {
                    method: "POST",
                    body: form,
                });
                successful.push(boardKey);
                delete archiveOperationIdsRef.current[boardKey];
            } catch (error: unknown) {
                failures[boardKey] = userFacingError(error, "We couldn’t save this board. Please try again.");
            }
        }

        setIsArchivingBoards(false);
        setArchiveFailures(failures);
        if (successful.length) {
            setSelectedReviewBoards((previous) => previous.filter((boardKey) => !successful.includes(boardKey)));
        }
        if (Object.keys(failures).length) {
            setNotice({
                variant: "error",
                title: "Some boards were not saved",
                message: "The unsuccessful boards remain selected so you can retry them. No duplicate Notes copies were created.",
            });
        } else {
            setNotice({
                variant: "info",
                title: "Saved to Notes",
                message: `${successful.length} breakout ${successful.length === 1 ? "board is" : "boards are"} now in Saved Collaboration Boards and available to this class.`,
            });
        }
    }

    async function handleStartNewSession() {
        const code = joinCodeRef.current;
        if (isArchivingBoards) return;
        if (code) {
            try {
                // Review keeps room history available for optional archive
                // saves.  Final close is deliberately deferred until the
                // teacher starts over, after any requested save has settled.
                await apiFetch(`${API_BASE}/collab/${encodeURIComponent(code)}/end-session`, { method: "POST" });
            } catch (error: unknown) {
                showError(error, "We couldn’t close this session yet. Your reviewed boards are still available to save.");
                return;
            }
        }
        stopPolling();
        joinCodeRef.current = "";
        teacherBoardExportRef.current = null;
        teacherBoardSnapshotRef.current = null;
        reviewBoardExportRefs.current = {};
        reviewBoardSnapshotImageRefs.current = {};
        archiveOperationIdsRef.current = {};
        focusedReviewBoardExportRef.current = null;

        setSessionState("draft");
        setIsPreparingNextRound(false);
        setShowJoinModal(false);
        setShowBreakoutModal(false);
        setTimeLeftSeconds(null);
        setSessionCode("");
        setStatus(null);
        setStatusError(null);
        setIsCreating(false);
        setIsStartingBreakout(false);
        setFocusedReviewBoard(null);
        setSelectedReviewBoards([]);
        setArchiveFailures({});
        setIsArchivingBoards(false);
        setBoardRound(1);
        setSelectedElumeTemplate(null);
        setTemplateRequestId(undefined);
        setTemplatePreview(null);
        setShowElumeTemplatesModal(false);
    }

    function handleFullReset() {
        handleStartNewSession();
        setSessionTitle("Collaboration Whiteboard");
        setTool("pen");
        setPenColor("black");
        setPenSize(1);
        setEraserSize(1);
        setHighlightColor("yellow");
        setRoomCount(4);
        setRoomCountInput("4");
        setRoomCountError(null);
        setTimerMinutes(10);
        setParticipants([]);
        setRooms(Array.from({ length: 4 }, (_, i) => ({ roomNumber: i + 1, participantIds: [] })));
        setReviewPanels([
            { id: uid("panel"), selectedBoard: "room-1" },
            { id: uid("panel"), selectedBoard: "room-2" },
            { id: uid("panel"), selectedBoard: "room-3" },
            { id: uid("panel"), selectedBoard: "room-4" },
        ]);
        setTeacherBoardClearNonce(0);
    }

    function dispatchScopedBoardClear(targetRoomKey: string, nonce: number) {
        window.dispatchEvent(
            new CustomEvent("collab-clear-board", {
                detail: { roomKey: targetRoomKey, nonce },
            })
        );
    }

    async function resetCurrentBoardRound() {
        const code = joinCodeRef.current;
        if (!code || isPreparingNextRound) return;
        setNotice(null);
        setIsPreparingNextRound(true);
        try {
            const data = await apiFetch(`${API_BASE}/collab/${code}/new-board`, { method: "POST" });
            setBoardRound(Math.max(1, Number(data?.board_round || boardRound + 1)));
            setSessionState("lobby");
            setFocusedReviewBoard(null);
            setTimeLeftSeconds(null);
            await fetchStatus(code);
        } catch (error: unknown) {
            showError(error, "We couldn’t prepare a new board just now. Please try again.");
        } finally {
            setIsPreparingNextRound(false);
        }
    }

    const assignedCount = participants.filter((p) => p.roomNumber !== null).length;
    const unassigned = participants.filter((p) => p.isOnline && p.roomNumber === null);
    const effectiveSessionState: SessionState = isPreparingNextRound ? "lobby" : sessionState;

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-24 top-[-60px] h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />
                <div className="absolute right-[-90px] top-12 h-96 w-96 rounded-full bg-violet-300/20 blur-3xl" />
                <div className="absolute bottom-[-90px] left-[10%] h-80 w-80 rounded-full bg-emerald-300/20 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#94a3b8_1px,transparent_1px),linear-gradient(to_bottom,#94a3b8_1px,transparent_1px)] [background-size:38px_38px]" />
            </div>

            <div className="relative z-10 p-4 md:p-6">
                <div className="mx-auto max-w-[1700px]">
                    {notice ? (
                        <div className="fixed inset-x-3 bottom-4 z-[80] mx-auto max-w-xl md:inset-x-auto md:right-6 md:w-[30rem]">
                            <InlineNotice
                                variant={notice.variant}
                                title={notice.title}
                                message={notice.message}
                                actionLabel="Dismiss"
                                onAction={() => setNotice(null)}
                            />
                        </div>
                    ) : null}
                    <div className={cls("mb-3 rounded-[22px] border border-white/70 bg-white/90 px-4 py-3 shadow-sm backdrop-blur-xl md:px-5", isTeacherFullscreen && "hidden")}>
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-white/70 bg-white shadow-md ring-1 ring-emerald-100">
                                        <img src={elumeLogo} alt="Elume" className="h-9 w-9 object-contain" />
                                    </div>

                                    <div>
                                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                                            Live collaboration
                                        </div>

                                        <h1 className="text-lg font-bold text-slate-900 md:text-xl">Collaboration Whiteboard</h1>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    {hasSession ? (
                                        <div
                                            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm"
                                            role="status"
                                            aria-live="polite"
                                        >
                                            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                                                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400/70 motion-reduce:animate-none animate-[ping_2.8s_ease-out_infinite]" />
                                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                                            </span>
                                            Session ready
                                        </div>
                                    ) : (
                                        isPreparingBoard ? (
                                            <button type="button" onClick={leaveBoardPreparation} className="min-h-11 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">{t("collab.exitPreparation")}</button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={createSession}
                                                disabled={isCreating}
                                                className={cls(
                                                    "min-h-11 rounded-2xl px-6 py-3 text-base font-black text-white shadow-lg transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200",
                                                    isCreating
                                                        ? "cursor-not-allowed bg-emerald-300"
                                                        : "bg-emerald-600 hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-xl"
                                                )}
                                            >
                                                {isCreating ? "Creating session..." : "Start"}
                                            </button>
                                        )
                                    )}

                                    {!hasSession && !isPreparingBoard && (
                                        <button type="button" onClick={beginBoardPreparation} className="min-h-11 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-800 shadow-sm hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100">
                                            {t("collab.prepareBoard")}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTemplateSubject("Teaching Templates");
                                            setTemplatePreview(null);
                                            setShowElumeTemplatesModal(true);
                                        }}
                                        className="min-h-11 rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-cyan-50 px-4 py-2 text-sm font-black text-violet-800 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-100"
                                    >
                                        Load Elume Template
                                    </button>

                                    {!hasSession && (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await fetchSavedBoards();
                                                setShowSavedBoardsModal(true);
                                            }}
                                            className="min-h-11 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-black text-violet-800 shadow-sm transition hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-100"
                                        >
                                            My Saved Boards
                                        </button>
                                    )}


                                    <button
                                        type="button"
                                        onClick={() => hasSession && setShowJoinModal(true)}
                                        disabled={!hasSession}
                                        className={cls(
                                            "min-h-11 rounded-2xl border px-4 py-2 text-sm font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200",
                                            hasSession
                                                ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 hover:shadow"
                                                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                        )}
                                    >
                                        Show QR Code
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowBreakoutModal(true)}
                                        disabled={!hasSession}
                                        className={cls(
                                            "relative min-h-11 rounded-2xl px-5 py-2 text-sm font-black text-white shadow-md transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200",
                                            hasSession
                                                ? "bg-emerald-600 hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-lg"
                                                : "cursor-not-allowed bg-emerald-300 text-emerald-50"
                                        )}
                                    >
                                        {hasSession && (
                                            <span className="pointer-events-none absolute -inset-1 rounded-[1.1rem] border border-emerald-400/70 motion-reduce:animate-none animate-[pulse_2.8s_ease-in-out_infinite]" aria-hidden="true" />
                                        )}
                                        Start Breakout
                                    </button>

                                    <button
                                        type="button"
                                        onClick={endBreakout}
                                        disabled={!hasSession || isArchivingBoards}
                                        className={cls(
                                            "relative min-h-11 rounded-2xl px-5 py-2 text-sm font-black text-white shadow-md transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-red-200",
                                            hasSession
                                                ? "bg-red-600 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg"
                                                : "cursor-not-allowed bg-red-200 text-red-50"
                                        )}
                                    >
                                        {hasSession && (
                                            <span className="pointer-events-none absolute -inset-1 rounded-[1.1rem] border border-red-400/70 motion-reduce:animate-none animate-[pulse_3.2s_ease-in-out_infinite]" aria-hidden="true" />
                                        )}
                                        End breakout
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleStartNewSession}
                                        disabled={!hasSession || isArchivingBoards}
                                        className="min-h-11 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-2 text-sm font-black text-white shadow-md transition hover:-translate-y-0.5 hover:from-violet-700 hover:to-purple-700 hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-200 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Start New Session
                                    </button>

                                    <button
                                        onClick={handleFullReset}
                                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                                    >
                                        Full reset
                                    </button>

                                    <button
                                        onClick={() => navigate(`/class/${classId}`)}
                                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-800 hover:bg-slate-50"
                                    >
                                        ← Back
                                    </button>

                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="max-w-xl text-sm text-slate-600">
                                    Live teacher board with student joining breakout rooms, and shared whiteboards.
                                </p>

                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        onClick={() => {
                                            const nextNonce = teacherBoardClearNonce + 1;
                                            setTeacherBoardClearNonce(nextNonce);
                                            dispatchScopedBoardClear("teacher-main", nextNonce);
                                        }}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-800 hover:bg-slate-50"
                                    >
                                        Erase all
                                    </button>

                                    <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
                                        State: {(isPreparingNextRound ? "LOBBY" : (status?.state || sessionState).toUpperCase())}
                                    </div>

                                    <div className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-800">
                                        Students: {status?.joined_count ?? participants.length}
                                    </div>

                                    <div className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-bold text-violet-800">
                                        Assigned: {status?.assigned_count ?? assignedCount}
                                    </div>

                                    <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-800">
                                        Rooms {roomCount}
                                    </div>

                                    <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
                                        Timer: {formatTime(status?.time_left_seconds ?? timeLeftSeconds)}
                                    </div>

                                    {statusError ? (
                                        <InlineNotice
                                            variant="warning"
                                            message={statusError}
                                            className="max-w-sm px-3 py-2 text-xs"
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        ref={teacherWorkspaceRef}
                        className={cls(
                            "relative grid grid-cols-1 gap-5",
                            !isTeacherFullscreen && "xl:grid-cols-[236px_minmax(0,1fr)]",
                            isTeacherFullscreen && "fixed inset-0 z-[60] grid-cols-[236px_minmax(0,1fr)] gap-4 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 xl:grid-cols-[236px_minmax(0,1fr)]"
                        )}
                        data-teacher-focus-mode={isTeacherFullscreen ? "active" : "inactive"}
                    >
                        <div className={cls("z-20 self-start", !isTeacherFullscreen && "sticky top-3")}>
                            <div className={cls("rounded-[28px] border border-slate-200/80 bg-white/95 p-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl", isTeacherFullscreen && "max-h-[calc(100dvh-2rem)] overflow-y-auto")}>
                                <TeacherToolPalette selectedTool={tool} onToolChange={(nextTool) => setTool(nextTool)} />

                                {(tool === "pen" || tool === "eraser" || tool === "highlighter") && <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-3">

                                    {tool === "pen" && (
                                        <div className="space-y-3">
                                            <div>
                                                <div className="mb-1 px-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Colour</div>
                                                <div className="flex flex-wrap gap-2">
                                                {(["black", "red", "blue", "yellow", "green", "purple"] as PenColor[]).map((c) => (
                                                    <ColourSwatch
                                                        key={c}
                                                        color={c}
                                                        label={c.charAt(0).toUpperCase() + c.slice(1)}
                                                        selected={penColor === c}
                                                        onClick={() => setPenColor(c)}
                                                    />
                                                ))}
                                                </div>
                                            </div>

                                            <div>
                                                <div className="mb-1 px-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Stroke</div>
                                                <div className="flex gap-2">
                                                {[{ value: 1 }, { value: 2 }, { value: 3 }].map(({ value }) => (
                                                    <StrokeSizeButton
                                                        key={value}
                                                        value={value as PenSize}
                                                        color={penColor}
                                                        selected={penSize === value}
                                                        onClick={() => setPenSize(value as PenSize)}
                                                    />
                                                ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {tool === "eraser" && (
                                        <div>
                                            <div className="mb-1 px-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Eraser size</div>
                                            <div className="flex gap-2">
                                            {[1, 2, 3].map((s) => (
                                                <StrokeSizeButton
                                                    key={s}
                                                    value={s as EraserSize}
                                                    selected={eraserSize === s}
                                                    onClick={() => setEraserSize(s as EraserSize)}
                                                />
                                            ))}
                                            </div>
                                        </div>
                                    )}

                                    {tool === "highlighter" && (
                                        <TeacherHighlighterSettings
                                            color={highlightColor}
                                            penSize={penSize}
                                            onColorChange={(color) => setHighlightColor(color as HighlightColor)}
                                            onPenSizeChange={setPenSize}
                                        />
                                    )}

                                </div>}

                                <div className="mt-3 space-y-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowBreakoutModal(true)}
                                        disabled={!hasSession}
                                        className={cls(
                                            "w-full rounded-2xl border px-3 py-3 text-xs font-black shadow-sm transition",
                                            hasSession
                                                ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                        )}
                                    >
                                        Breakout rooms
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => hasSession && setShowJoinModal(true)}
                                        disabled={!hasSession}
                                        className={cls(
                                            "w-full rounded-2xl border px-3 py-3 text-xs font-black shadow-sm transition",
                                            hasSession
                                                ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                        )}
                                    >
                                        Show QR Code
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={cls("min-w-0 space-y-5", isTeacherFullscreen && "min-h-0")}>
                            <SectionCard
                                title={sessionTitle}
                                hint="Teacher board stage"
                                className={cls("min-w-0", isTeacherFullscreen && "flex h-full min-h-0 flex-col rounded-[28px] p-4")}
                                bodyClassName={cls("min-w-0", isTeacherFullscreen && "min-h-0 flex-1")}
                                right={
                                    <div className="flex flex-wrap items-center gap-2">
                                        {hasSession && effectiveSessionState !== "review" && (
                                            <button
                                                ref={fullscreenButtonRef}
                                                type="button"
                                                onClick={isTeacherFullscreen ? exitTeacherFullscreen : enterTeacherFullscreen}
                                                aria-label={isTeacherFullscreen ? "Exit Full Screen" : "Full Screen"}
                                                className="inline-flex items-center gap-2 rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-cyan-50 px-4 py-2 text-xs font-black text-violet-800 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-100"
                                            >
                                                {isTeacherFullscreen ? <Minimize2 size={16} aria-hidden="true" /> : <Maximize2 size={16} aria-hidden="true" />}
                                                {isTeacherFullscreen ? "Exit Full Screen" : "Full Screen"}
                                            </button>
                                        )}
                                        {hasSession && (
                                            <button
                                                type="button"
                                                onClick={() => setStudentsDrawerOpen((open) => !open)}
                                                aria-expanded={studentsDrawerOpen}
                                                aria-controls="collaboration-students-drawer"
                                                className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-gradient-to-r from-cyan-50 via-white to-emerald-50 px-4 py-2 text-xs font-black text-cyan-800 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-100"
                                            >
                                                <UsersRound size={16} aria-hidden="true" />
                                                Students {status?.joined_count ?? participants.length}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setPdfImportRequestNonce((n) => n + 1)}
                                            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                        >
                                            Import PDF
                                        </button>

                                        {isPreparingBoard && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (demoReadOnly) {
                                                        explainDemoSaveRestriction();
                                                        return;
                                                    }
                                                    setSavedBoardTitle(sessionTitle);
                                                    setShowSaveBoardModal(true);
                                                }}
                                                className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-2 text-xs font-black text-violet-800 shadow-sm hover:bg-violet-100"
                                            >
                                                {preparedBoardId ? t("collab.saveBoardChanges") : t("collab.savePreparedBoard")}
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={downloadTeacherPng}
                                            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                        >
                                            Download PNG
                                        </button>
                                    </div>
                                }
                            >
                                {effectiveSessionState !== "review" ? (
                                    <div className={cls("relative", isTeacherFullscreen && "h-full")}>
                                        {hasSession || isPreparingBoard ? (
                                            <CollabBoard
                                                key={`teacher-board-${isPreparingBoard ? `prepare-${boardEditorGeneration}` : joinCode}`}
                                                sessionCode={isPreparingBoard ? "" : joinCode}
                                                roomKey="teacher-main"
                                                boardRound={boardRound}
                                                participantId="teacher"
                                                tool={
                                                    tool === "highlighter"
                                                        ? "highlighter"
                                                        : tool === "eraser"
                                                            ? "eraser"
                                                            : tool === "select"
                                                                ? "select"
                                                                : tool === "rectangle"
                                                                    ? "rectangle"
                                                                    : tool === "circle"
                                                                        ? "circle"
                                                                        : tool === "triangle"
                                                                            ? "triangle"
                                                                            : tool === "sticky"
                                                                                ? "sticky"
                                                                                : tool === "arrow"
                                                                                    ? "arrow"
                                                                                    : tool === "curved-arrow"
                                                                                        ? "curved-arrow"
                                                                                        : tool === "speech"
                                                                                            ? "speech"
                                                                                            : "pen"
                                                }
                                                penColor={penColor}
                                                penSize={penSize}
                                                highlighterColor={highlightColor}
                                                eraserSize={eraserSize}
                                                height={isTeacherFullscreen ? teacherFullscreenHeight : 760}
                                                classId={String(classId)}
                                                apiBase={API_BASE}
                                                apiFetch={apiFetch}
                                                pdfImportRequestNonce={pdfImportRequestNonce}
                                                localOnly={isPreparingBoard}
                                                preparationLabel={t("collab.preparationLabel")}
                                                initialSnapshot={isPreparingBoard ? preparedBoardSnapshot : null}
                                                onExportReady={(fn) => {
                                                    teacherBoardExportRef.current = fn;
                                                }}
                                                onSnapshotReady={(getSnapshot) => {
                                                    teacherBoardSnapshotRef.current = getSnapshot;
                                                }}
                                                templateBackground={selectedElumeTemplate ? {
                                                    templateId: selectedElumeTemplate.id,
                                                    title: selectedElumeTemplate.title,
                                                    src: selectedElumeTemplate.src,
                                                } : null}
                                                templateRequestId={templateRequestId}
                                                boardWidth={1600}
                                                boardHeight={1200}
                                            />
                                        ) : (
                                            <div className="min-h-[760px] rounded-[28px] border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-5 sm:p-6">
                                                <div className="mx-auto w-full max-w-2xl text-center">
                                                    <p className="text-sm font-semibold text-orange-900 sm:text-base">
                                                        {t("collab.startGuidance")}
                                                    </p>

                                                    <div className="relative mx-auto mt-3 aspect-[3/2] w-full max-w-xl overflow-hidden rounded-[24px] bg-orange-500 shadow-[0_16px_32px_rgba(234,88,12,0.28)]">
                                                        <img src={collabBoardStartArt} alt="" aria-hidden="true" className="h-full w-full object-contain" />
                                                        <button
                                                            className="absolute left-[35%] top-[34%] h-[21.5%] w-[31%] rounded-[clamp(0.65rem,2.2vw,1.35rem)] bg-white px-2 text-center text-[clamp(0.7rem,1.7vw,1.05rem)] font-black leading-tight text-orange-800 shadow-[0_3px_0_rgba(154,52,18,0.22)] transition hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-orange-600 disabled:cursor-not-allowed disabled:bg-orange-100 disabled:text-orange-800 disabled:opacity-100"
                                                            type="button"
                                                            onClick={createSession}
                                                            disabled={isCreating}
                                                            aria-label={isCreating ? t("collab.creatingSession") : t("collab.clickHereToStart")}
                                                        >
                                                            {isCreating ? t("collab.creatingSession") : t("collab.clickHereToStart")}
                                                        </button>
                                                    </div>

                                                    {selectedElumeTemplate ? (
                                                        <div className="mx-auto mt-5 max-w-md overflow-hidden rounded-2xl border border-violet-200 bg-white p-3 text-left shadow-sm">
                                                            <div className="flex items-center gap-3">
                                                                <img src={selectedElumeTemplate.src} alt="" className="h-16 w-24 rounded-lg border border-slate-100 bg-slate-50 object-contain" />
                                                                <div>
                                                                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-violet-700">Template ready</div>
                                                                    <div className="mt-1 text-sm font-black text-slate-900">{selectedElumeTemplate.title}</div>
                                                                    <div className="mt-1 text-xs text-slate-600">It will be added as the locked 1600 × 1200 board background when you start.</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : null}

                                                </div>
                                            </div>

                                        )}

                                        {timeLeftSeconds !== null && effectiveSessionState === "live" && (
                                            <div className="absolute right-4 top-4 rounded-full border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-black text-violet-800 shadow-sm">
                                                Timer: {formatTime(timeLeftSeconds)}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* A full-size hidden renderer per room gives the Notes archive the
                                            same faithful PNG as Download PNG, including ink, text, images
                                            and a template background. It is mounted only during review. */}
                                        <div className="pointer-events-none fixed -left-[100000px] top-0 opacity-0" aria-hidden="true">
                                            {hasSession && breakoutBoardOptions.map((board) => (
                                                <CollabBoard
                                                    key={`archive-render-${board.value}`}
                                                    sessionCode={joinCode}
                                                    roomKey={board.value}
                                                    boardRound={boardRound}
                                                    participantId={`review-archive-${board.value}`}
                                                    tool="select"
                                                    penColor={penColor}
                                                    penSize={penSize}
                                                    highlighterColor={highlightColor}
                                                    eraserSize={eraserSize}
                                                    height={760}
                                                    readOnly
                                                    viewportMode="pan"
                                                    boardWidth={1600}
                                                    boardHeight={1200}
                                                    onSnapshotImageReady={(render) => {
                                                        reviewBoardSnapshotImageRefs.current[board.value] = render;
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        <div className="rounded-[28px] border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-4 shadow-sm">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-black text-slate-900">Ready for another round</div>
                                                    <div className="mt-1 text-sm text-slate-600">Review each room, then optionally save selected boards as class Notes before ending or starting another round.</div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={resetCurrentBoardRound}
                                                    disabled={isPreparingNextRound || isArchivingBoards}
                                                    className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-700 shadow-sm hover:bg-emerald-100"
                                                >
                                                    {isPreparingNextRound ? "Preparing…" : "New Board"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (demoReadOnly) {
                                                            explainDemoSaveRestriction();
                                                            return;
                                                        }
                                                        setSavedBoardTitle(sessionTitle);
                                                        setShowSaveBoardModal(true);
                                                    }}
                                                    className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-black text-violet-800 shadow-sm hover:bg-violet-100"
                                                >
                                                    Save for Later
                                                </button>
                                            </div>
                                        </div>

                                        <section aria-label="Save selected breakout boards to Notes" className="rounded-[28px] border border-violet-200 bg-violet-50/70 p-4 shadow-sm">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-black text-violet-950">Save selected to Notes</div>
                                                    <p className="mt-1 text-sm text-violet-900">Saved copies are available to the class in <strong>Saved Collaboration Boards</strong>. If a board cannot be rendered faithfully, it stays selected and shows a retry message instead of saving a partial copy.</p>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <button type="button" onClick={selectAllReviewBoards} disabled={isArchivingBoards} className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-xs font-black text-violet-800 shadow-sm hover:bg-violet-50 disabled:opacity-60">Select all</button>
                                                    <button type="button" onClick={clearReviewBoardSelection} disabled={isArchivingBoards || selectedReviewBoards.length === 0} className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-xs font-black text-violet-800 shadow-sm hover:bg-violet-50 disabled:opacity-60">Clear selection</button>
                                                    <button type="button" onClick={saveSelectedReviewBoardsToNotes} disabled={isArchivingBoards || (!demoReadOnly && selectedReviewBoards.length === 0)} className="rounded-xl bg-violet-700 px-4 py-2 text-xs font-black text-white shadow-sm hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-60">
                                                        {isArchivingBoards ? "Saving selected boards…" : `Save ${selectedReviewBoards.length} selected to Notes`}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                                {breakoutBoardOptions.map((board) => {
                                                    const selected = selectedReviewBoards.includes(board.value);
                                                    const failure = archiveFailures[board.value];
                                                    return <label key={`archive-select-${board.value}`} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-violet-100 bg-white px-3 py-3 text-sm text-slate-800 shadow-sm">
                                                        <input type="checkbox" checked={selected} onChange={() => toggleReviewBoardArchive(board.value)} disabled={isArchivingBoards} className="mt-0.5 h-4 w-4 accent-violet-700" />
                                                        <span><span className="block font-black">{board.label}</span>{failure ? <span role="alert" className="mt-1 block text-xs font-semibold text-rose-700">{failure}</span> : null}</span>
                                                    </label>;
                                                })}
                                            </div>
                                        </section>

                                        {focusedReviewBoard ? (
                                    <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                                        <div className="mb-4 flex items-center justify-between gap-3">
                                            <div className="text-sm font-black text-slate-900">
                                                {boardOptions.find((b) => b.value === focusedReviewBoard)?.label || "Board"}
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={downloadFocusedReviewBoardPng}
                                                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                                >
                                                    Download PNG
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => setFocusedReviewBoard(null)}
                                                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                                >
                                                    Back to 2×2 grid
                                                </button>
                                            </div>
                                        </div>

                                        <div className="relative min-h-[760px] overflow-hidden rounded-[24px] border border-slate-200 bg-white">
                                            <div className="absolute left-3 top-3 z-10 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-700">
                                                {boardOptions.find((b) => b.value === focusedReviewBoard)?.label || "Board"}
                                            </div>

                                            {hasSession ? (
                                                <CollabBoard
                                                    key={`focused-${focusedReviewBoard}`}
                                                    sessionCode={joinCode}
                                                    roomKey={focusedReviewBoard}
                                                    boardRound={boardRound}
                                                    participantId="teacher-review-focus"
                                                    tool="select"
                                                    penColor={penColor}
                                                    penSize={penSize}
                                                    highlighterColor={highlightColor}
                                                eraserSize={eraserSize}
                                                height={760}
                                                readOnly
                                                viewportMode="pan"
                                                boardWidth={1600}
                                                boardHeight={1200}
                                                showZoomControls
                                                onExportReady={(fn) => {
                                                    focusedReviewBoardExportRef.current = fn;
                                                }}
                                                />

                                            ) : (
                                                <div className="grid min-h-[760px] place-items-center text-slate-500">No session.</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                        {reviewPanels.map((panel, idx) => (
                                            <div key={panel.id} className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                                                <div className="mb-3 flex items-center justify-between gap-3">
                                                    <div className="text-sm font-black text-slate-900">Review Panel {idx + 1}</div>

                                                    <div className="flex items-center gap-2">
                                                        {panel.selectedBoard.startsWith("room-") && (
                                                            <label className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-black text-violet-800">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedReviewBoards.includes(panel.selectedBoard)}
                                                                    onChange={() => toggleReviewBoardArchive(panel.selectedBoard)}
                                                                    disabled={isArchivingBoards}
                                                                    className="h-4 w-4 accent-violet-700"
                                                                />
                                                                Save to Notes
                                                            </label>
                                                        )}
                                                        <select
                                                            value={panel.selectedBoard}
                                                            onChange={(e) =>
                                                                setReviewPanels((prev) =>
                                                                    prev.map((p) => (p.id === panel.id ? { ...p, selectedBoard: e.target.value } : p))
                                                                )
                                                            }
                                                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 shadow-sm outline-none"
                                                        >
                                                            {boardOptions.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        <button
                                                            type="button"
                                                            onClick={() => setFocusedReviewBoard(panel.selectedBoard)}
                                                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                                        >
                                                            Expand
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadReviewBoardPng(panel.selectedBoard)}
                                                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                                        >
                                                            Download PNG
                                                        </button>

                                                    </div>
                                                </div>

                                                <div className="relative min-h-[300px] overflow-hidden rounded-[24px] border border-slate-200 bg-white">
                                                    <div className="absolute left-3 top-3 z-10 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-700">
                                                        {boardOptions.find((b) => b.value === panel.selectedBoard)?.label || "Board"}
                                                    </div>

                                                    {hasSession ? (
                                                        <>
                                                            <CollabBoard
                                                                key={`${panel.id}-${panel.selectedBoard}`}
                                                                sessionCode={joinCode}
                                                                roomKey={panel.selectedBoard}
                                                                boardRound={boardRound}
                                                                participantId={`review-${panel.id}`}
                                                                tool="select"
                                                                penColor={penColor}
                                                                penSize={penSize}
                                                                highlighterColor={highlightColor}
                                                                eraserSize={eraserSize}
                                                                height={300}
                                                                readOnly
                                                                viewportMode="pan"
                                                                boardWidth={1600}
                                                                boardHeight={1200}
                                                            />

                                                            <div className="pointer-events-none absolute -left-[99999px] top-0 opacity-0">
                                                                <>
                                                                    <CollabBoard
                                                                        key={`${panel.id}-${panel.selectedBoard}`}
                                                                        sessionCode={joinCode}
                                                                        roomKey={panel.selectedBoard}
                                                                        boardRound={boardRound}
                                                                        participantId={`review-${panel.id}`}
                                                                        tool="select"
                                                                        penColor={penColor}
                                                                        penSize={penSize}
                                                                        highlighterColor={highlightColor}
                                                                        eraserSize={eraserSize}
                                                                        height={300}
                                                                        readOnly
                                                                        viewportMode="pan"
                                                                        boardWidth={1600}
                                                                        boardHeight={1200}
                                                                    />

                                                                    <div className="pointer-events-none absolute -left-[99999px] top-0 opacity-0">
                                                                        <CollabBoard
                                                                            key={`export-${panel.id}-${panel.selectedBoard}`}
                                                                            sessionCode={joinCode}
                                                                            roomKey={panel.selectedBoard}
                                                                            boardRound={boardRound}
                                                                            participantId={`review-export-${panel.id}`}
                                                                            tool="select"
                                                                            penColor={penColor}
                                                                            penSize={penSize}
                                                                            highlighterColor={highlightColor}
                                                                            eraserSize={eraserSize}
                                                                            height={760}
                                                                            readOnly
                                                                            viewportMode="pan"
                                                                            boardWidth={1600}
                                                                            boardHeight={1200}
                                                                            onExportReady={(fn) => {
                                                                                reviewBoardExportRefs.current[panel.selectedBoard] = fn;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </>

                                                            </div>
                                                        </>

                                                    ) : (
                                                        <div className="grid min-h-[300px] place-items-center text-slate-500">No session.</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                    </div>
                                )
                                }
                            </SectionCard>
                        </div>

                        {studentsDrawerOpen && <SectionCard
                            id="collaboration-students-drawer"
                            className="absolute right-0 top-0 z-50 max-h-[calc(100dvh-2rem)] w-full max-w-[320px] overflow-y-auto border-cyan-100 bg-white/95 shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
                            title="Students"
                            hint="Joined participants and room assignments"
                            right={
                                <button
                                    id="collaboration-students-drawer-close"
                                    type="button"
                                    onClick={() => setStudentsDrawerOpen(false)}
                                    aria-label="Close Students"
                                    className="grid h-10 w-10 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-100"
                                >
                                    <X size={18} aria-hidden="true" />
                                </button>
                            }
                        >
                            <div className="mb-4 rounded-[28px] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-4 shadow-sm">
                                <div className="text-sm font-black text-slate-900">Session title</div>
                                <input
                                    value={sessionTitle}
                                    onChange={(e) => setSessionTitle(e.target.value)}
                                    className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                                />
                            </div>

                            <div className="space-y-3">
                                {participants.map((p) => (
                                    <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-black text-slate-900">{p.name}</div>
                                                <div className="mt-1 text-xs font-semibold text-slate-500">{p.isOnline ? "Online" : "Offline"}</div>
                                            </div>

                                            <div
                                                className={cls(
                                                    "rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em]",
                                                    p.roomNumber
                                                        ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                                                        : "border border-slate-200 bg-slate-50 text-slate-600"
                                                )}
                                            >
                                                {p.roomNumber ? `Room ${p.roomNumber}` : "Unassigned"}
                                            </div>
                                        </div>

                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {Array.from({ length: roomCount }).map((_, i) => {
                                                const room = i + 1;
                                                const isAssigned = p.roomNumber === room;

                                                return (
                                                    <button
                                                        key={room}
                                                        type="button"
                                                        onClick={() => assignAndSave(p.id, room)}
                                                        className={cls(
                                                            "rounded-xl border px-3 py-2 text-xs font-black",
                                                            isAssigned
                                                                ? "border-emerald-300 bg-emerald-500 text-white"
                                                                : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                                                        )}
                                                    >
                                                        Room {room}
                                                    </button>
                                                );
                                            })}

                                            <button
                                                type="button"
                                                onClick={() => assignAndSave(p.id, null)}
                                                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-100"
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SectionCard>}
                    </div>
                </div>
            </div>

            {showElumeTemplatesModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-sm md:p-6" role="dialog" aria-modal="true" aria-labelledby="elume-template-library-title">
                    <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
                        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-5 md:px-7">
                            <div>
                                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-violet-700">Elume library</div>
                                <h2 id="elume-template-library-title" className="mt-1 text-2xl font-black tracking-tight text-slate-900">Elume Collaboration Templates</h2>
                                <p className="mt-2 text-sm text-slate-600">Choose a ready-made activity for your collaboration board.</p>
                            </div>
                            <button type="button" onClick={() => { setTemplatePreview(null); setShowElumeTemplatesModal(false); }} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-600 shadow-sm hover:bg-slate-50">Close</button>
                        </div>

                        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5 md:flex-row md:p-7">
                            <div className="flex shrink-0 gap-2 overflow-x-auto pb-1 md:w-44 md:flex-col md:overflow-visible">
                                {COLLABORATION_TEMPLATE_SUBJECTS.map((subject) => {
                                    const isTeachingTemplates = subject === "Teaching Templates";
                                    return (
                                        <button
                                            key={subject}
                                            type="button"
                                            onClick={() => { setTemplateSubject(subject); setTemplatePreview(null); }}
                                            className={cls(
                                                "min-h-11 shrink-0 rounded-2xl px-4 py-3 text-left text-sm font-black transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-100",
                                                isTeachingTemplates
                                                    ? templateSubject === subject
                                                        ? "bg-gradient-to-r from-violet-600 via-cyan-600 to-emerald-500 text-white shadow-lg"
                                                        : "border border-cyan-200 bg-gradient-to-r from-violet-50 via-cyan-50 to-emerald-50 text-slate-800 shadow-sm hover:border-violet-300 hover:shadow-md"
                                                    : templateSubject === subject
                                                        ? "bg-gradient-to-r from-violet-600 to-cyan-600 text-white shadow-md"
                                                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-violet-50"
                                            )}
                                        >
                                            {subject}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => selectElumeTemplate(null)}
                                    className="min-h-11 shrink-0 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:bg-slate-50"
                                >
                                    Blank Board
                                </button>
                            </div>

                            <div className="min-w-0 flex-1">
                                <div className="mb-4 flex items-center justify-between gap-3">
                                    <div className="text-lg font-black text-slate-900">{templateSubject}</div>
                                    <div className="rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-black text-violet-700">5 templates</div>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                    {templatesForSubject(templateSubject).map((template) => (
                                        <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => setTemplatePreview(template)}
                                            className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-100"
                                        >
                                            <div className="aspect-[4/3] bg-slate-50 p-2">
                                                <img src={template.src} alt="" className="h-full w-full object-contain" />
                                            </div>
                                            <div className="border-t border-slate-100 px-4 py-3 text-sm font-black text-slate-900">{template.title}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {templatePreview ? (
                            <div className="border-t border-violet-100 bg-gradient-to-r from-violet-50 via-white to-cyan-50 p-5 md:px-7">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                                    <div className="aspect-[4/3] w-full shrink-0 overflow-hidden rounded-2xl border border-white bg-white p-2 shadow-sm sm:w-[360px]">
                                        <img src={templatePreview.src} alt="" className="h-full w-full object-contain" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[11px] font-black uppercase tracking-[0.16em] text-violet-700">{templatePreview.subject}</div>
                                        <div className="mt-1 text-lg font-black text-slate-900">{templatePreview.title}</div>
                                        <div className="mt-1 text-sm text-slate-600">Locked 1600 × 1200 background. Draw and add notes above it.</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setTemplatePreview(null)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Cancel</button>
                                        <button type="button" onClick={() => selectElumeTemplate(templatePreview)} className="rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 px-5 py-3 text-sm font-black text-white shadow-md hover:from-violet-700 hover:to-cyan-700">Use Template</button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {showSavedBoardsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-2xl rounded-[32px] border border-white/70 bg-white/95 p-6 shadow-[0_25px_80px_rgba(15,23,42,0.20)]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-700">My Saved Boards</div>
                                <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-900">Start from a proven activity</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-600">Each use creates a fresh collaboration session for this class.</p>
                            </div>
                            <button type="button" onClick={() => setShowSavedBoardsModal(false)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-50">Close</button>
                        </div>
                        <div className="mt-5 max-h-[55vh] space-y-3 overflow-y-auto pr-1">
                            {savedBoards.length ? savedBoards.map((template) => (
                                <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                    <div>
                                        <div className="font-black text-slate-900">{template.title}</div>
                                        <div className="mt-1 text-xs font-semibold text-slate-500">{template.room_count} room{template.room_count === 1 ? "" : "s"}</div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button type="button" onClick={() => void launchSavedBoard(template)} disabled={isUsingSavedBoard} className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-violet-700 disabled:opacity-50">Use</button>
                                        <button type="button" onClick={() => setSharingTemplateId(template.id)} className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800">Share</button>
                                        <button type="button" onClick={() => void editSavedBoard(template)} disabled={isUsingSavedBoard} className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-black text-sky-800 disabled:opacity-50">{t("collab.editBoardCopy")}</button>
                                        <button type="button" onClick={() => void renameSavedBoard(template)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50">Rename</button>
                                        <button type="button" onClick={() => void deleteSavedBoard(template)} className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-black text-red-700 hover:bg-red-50">Delete</button>
                                    </div>
                                </div>
                            )) : (
                                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm font-semibold text-slate-500">No saved boards yet. After a breakout, choose Save for Later to keep the clean teacher setup.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showSaveBoardModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-lg rounded-[32px] border border-white/70 bg-white/95 p-6 shadow-[0_25px_80px_rgba(15,23,42,0.20)]">
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-700">{isPreparingBoard ? "Prepare a board" : "Save for Later"}</div>
                        <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-900">{isPreparingBoard ? t("collab.preparedBoardHeading") : "Save the clean activity setup"}</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600">{isPreparingBoard ? t("collab.preparedBoardHelp") : "Student work and room assignments are not included."}</p>
                        <label className="mt-5 block text-sm font-black text-slate-800">Board/activity name</label>
                        <input autoFocus value={savedBoardTitle} onChange={(event) => setSavedBoardTitle(event.target.value)} maxLength={255} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100" />
                        <div className="mt-5 flex justify-end gap-3">
                            <button type="button" onClick={() => setShowSaveBoardModal(false)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button type="button" onClick={() => void saveBoardForLater()} disabled={!savedBoardTitle.trim() || isSavingBoard} className="rounded-2xl bg-violet-600 px-4 py-3 text-sm font-black text-white shadow-sm hover:bg-violet-700 disabled:opacity-50">{isSavingBoard ? "Saving…" : "Save Board"}</button>
                        </div>
                    </div>
                </div>
            )}
            {sharingTemplateId !== null && <DepartmentShareModal resource="template" resourceId={sharingTemplateId} onClose={() => setSharingTemplateId(null)} />}

            {showJoinModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
                    <div className="max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-[32px] border border-white/70 bg-white shadow-[0_25px_80px_rgba(15,23,42,0.20)]">
                        <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-orange-600 to-amber-500 px-5 py-4 text-white sm:px-6">
                            <div className="flex min-w-0 items-center gap-3">
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">{t("teacherInvite.studentJoin")}</div>
                                    <h3 className="mt-1 text-2xl font-black tracking-tight text-white">{t("teacherInvite.collabTitle")}</h3>
                                </div>
                                <CollabGroupArt className="w-20 sm:!w-24" />
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowJoinModal(false)}
                                className="rounded-xl border border-white/40 bg-white/15 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70"
                            >
                                {t("common.close")}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-5 p-5 sm:p-6 lg:grid-cols-[280px_1fr]">
                            <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5 shadow-sm">
                                <div className="mx-auto rounded-[28px] border border-white/80 bg-white p-4 shadow-lg">
                                    <img
                                        src={joinUrl ? qrUrl : "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="}
                                        alt="Join QR"
                                        className="mx-auto h-[220px] w-[220px] rounded-2xl border border-slate-100 bg-white"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <JoinChip
                                    label={t("teacherInvite.boardCode")}
                                    value={joinCode || "Create session first"}
                                    onCopy={() => joinCode && copyToClipboard(joinCode)}
                                    large
                                />

                                <JoinChip label={t("teacherInvite.joinLink")} value={joinUrl || "Create session first"} onCopy={() => joinUrl && copyToClipboard(joinUrl)} />

                                <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm leading-6 text-orange-950">
                                    {t("teacherInvite.collabInstruction")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showBreakoutModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white/95 shadow-[0_25px_80px_rgba(15,23,42,0.20)]">
                        <div className="border-b border-slate-100 px-6 py-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-700">Breakout Rooms</div>
                                <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-900">Setup breakout collaboration</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                    Choose room count, assign students, start timer, then launch room boards copied from the teacher board.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowBreakoutModal(false)}
                                className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-50"
                            >
                                Close
                            </button>
                        </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
                            <div className="space-y-5">
                                <div className="rounded-[28px] border border-violet-100 bg-gradient-to-br from-violet-50 via-white to-cyan-50 p-5 shadow-sm">
                                    <div className="text-sm font-black text-slate-900">Room settings</div>

                                    <div className="mt-4">
                                        <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                                            Number of rooms
                                        </div>

                                        <div className="mt-2 flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => adjustRoomCount(-1)}
                                                disabled={sessionState === "live" || sessionState === "review" || sessionState === "ended" || roomCount <= MIN_ROOM_COUNT}
                                                aria-label={t("collab.decreaseRoomCount")}
                                                className="grid min-h-12 min-w-12 place-items-center rounded-2xl border border-slate-200 bg-white text-xl font-black text-slate-800 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100 disabled:cursor-not-allowed disabled:text-slate-400"
                                            >
                                                −
                                            </button>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                aria-invalid={Boolean(roomCountError)}
                                                aria-describedby={roomCountError ? "collab-room-count-error" : undefined}
                                                value={roomCountInput}
                                                disabled={sessionState === "live" || sessionState === "review" || sessionState === "ended"}
                                                onChange={(e) => {
                                                    if (sessionState === "live" || sessionState === "review" || sessionState === "ended") return;
                                                    setRoomCountInput(e.target.value.replace(/[^0-9]/g, ""));
                                                    setRoomCountError(null);
                                                }}
                                                onBlur={() => { commitRoomCount(); }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        commitRoomCount();
                                                    }
                                                }}
                                                className="min-h-12 min-w-0 flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-lg font-black text-slate-800 shadow-sm outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => adjustRoomCount(1)}
                                                disabled={sessionState === "live" || sessionState === "review" || sessionState === "ended" || roomCount >= MAX_ROOM_COUNT}
                                                aria-label={t("collab.increaseRoomCount")}
                                                className="grid min-h-12 min-w-12 place-items-center rounded-2xl border border-slate-200 bg-white text-xl font-black text-slate-800 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100 disabled:cursor-not-allowed disabled:text-slate-400"
                                            >
                                                +
                                            </button>
                                        </div>
                                        {roomCountError ? <p id="collab-room-count-error" className="mt-2 text-sm font-semibold text-rose-700">{roomCountError}</p> : <p className="mt-2 text-xs text-slate-500">{t("collab.roomCountRange")}</p>}

                                    </div>

                                    <div className="mt-4">
                                        <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Timer minutes</div>
                                        <input
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={timerMinutes}
                                            onChange={(e) => setTimerMinutes(Math.max(1, Math.min(60, Number(e.target.value || 1))))}
                                            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </div>

                                    <div className="mt-5 grid grid-cols-1 gap-3">
                                        <button
                                            type="button"
                                            onClick={randomEvenSplit}
                                            className="rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-4 py-3 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
                                        >
                                            Random even split
                                        </button>

                                        <button
                                            type="button"
                                            onClick={clearAssignments}
                                            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
                                        >
                                            Clear assignments
                                        </button>

                                        <button
                                            type="button"
                                            onClick={saveAssignments}
                                            disabled={!hasSession}
                                            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                        >
                                            Save assignments
                                        </button>

                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    await startBreakoutAndPersist();
                                                } catch (error: unknown) {
                                                    showStartBreakoutFailure(error);
                                                }
                                            }}
                                            disabled={!hasSession || isStartingBreakout}
                                            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                        >
                                            {isStartingBreakout ? "Starting breakout..." : "Start breakout session"}
                                        </button>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                                    <div className="text-sm font-black text-slate-900">Unassigned students</div>
                                    <div className="mt-3 space-y-2">
                                        {unassigned.length ? (
                                            unassigned.map((p) => (
                                                <div key={p.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-800">
                                                    {p.name}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                                                Everyone assigned.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {rooms.map((room) => {
                                    const roomParticipants = participants.filter((p) => p.roomNumber === room.roomNumber);

                                    return (
                                        <div key={room.roomNumber} className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="text-base font-black text-slate-900">Room {room.roomNumber}</div>
                                                <div className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-cyan-700">
                                                    {roomParticipants.length} students
                                                </div>
                                            </div>

                                            <div className="mt-4 space-y-2">
                                                {roomParticipants.length ? (
                                                    roomParticipants.map((p) => (
                                                        <div key={p.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-800">
                                                            {p.name}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
                                                        No students assigned yet.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        </div>
                        <div className="sticky bottom-0 border-t border-slate-100 bg-white/95 px-6 py-4">
                            <div className="flex flex-wrap items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowBreakoutModal(false)}
                                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800 shadow-sm hover:bg-slate-50"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await startBreakoutAndPersist();
                                        } catch (error: unknown) {
                                            showStartBreakoutFailure(error);
                                        }
                                    }}
                                    disabled={!hasSession || isStartingBreakout}
                                    className="rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-5 py-3 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isStartingBreakout ? "Starting breakout..." : "Start breakout session"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {false && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-3xl rounded-[32px] border border-white/70 bg-white/95 p-6 shadow-[0_25px_80px_rgba(15,23,42,0.20)]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">PDF Import</div>
                                <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-900">Import from notes / PDF source</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                    This modal is where your current whiteboard PDF picker, page preview, and snipping flow can be dropped in.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => { }}
                                className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-50"
                            >
                                Close
                            </button>
                        </div>

                        <div className="mt-6 rounded-[28px] border border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 p-8 text-center shadow-sm">
                            <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl border border-white/80 bg-white shadow-lg">
                                <span className="text-3xl">📄</span>
                            </div>

                            <div className="mt-5 text-xl font-black text-slate-900">PDF workflow placeholder</div>
                            <div className="mt-2 text-sm leading-6 text-slate-600">
                                Reuse your existing PDF import, page rendering, and snip-to-board logic here.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
